import React, { useState, memo } from "react";
import * as arrow from "apache-arrow";
import { convertToDatetimeLocal } from "../../../../ECLInputs/ECLDatePicker/DateHelpers";

const EditableTableCell = memo((props) => {
   const content =
      props.content !== null && props.content !== undefined
         ? props.content
         : "";
   const [tempValue, setTempValue] = useState(content); // Temporary local value

   const handleFocus = () => {
      setTempValue(props.content); // Reset tempValue to content on focus
   };

   const handleBlur = async () => {
      await commitChanges(); // Ensure async completion
   };

   const handleChange = (e) => {
      const inputValue = e.target.value;
      switch (props.typeId) {
         case arrow.Type.Utf8: {
            // Allow any text for string input
            setTempValue(inputValue);
            break;
         }
         case arrow.Type.Int: {
            // Allow only valid integer characters (+, -, digits, and optionally .)
            if (/^[+\-*/.\d]*$/.test(inputValue)) {
               setTempValue(inputValue); // Update state with valid input
            }
            break;
         }
         case arrow.Type.Float: {
            // Allow only valid float characters (+, -, digits, ., and optionally e for scientific notation)
            if (/^[+\-*/.\deE]*$/.test(inputValue)) {
               setTempValue(inputValue); // Update state with valid input
            }
            break;
         }
         default: {
            console.error(`Unsupported typeId: ${props.typeId}`);
            break;
         }
      }
   };

   const handleKeyDown = async (e) => {
      if (e.key === "Enter") {
         await commitChanges(); // Ensure async completion
      }
   };

   const sanitizeMathExpression = async (expression) => {
      // Validate expression using a regex
      const validExpression = /^[+\-*/.\d\s()]+$/.test(expression); // Allow only valid characters
      const noConsecutiveOperators = !/[+\-*/]{2,}/.test(expression); // Disallow consecutive operators (e.g., `+*`, `-+`)
      const noTrailingOperator = !/[+\-*/]$/.test(expression); // Disallow trailing operators (e.g., `6+*`)
      let evaluatedValue;
      if (!validExpression || !noConsecutiveOperators || !noTrailingOperator) {
         evaluatedValue = 0;
      } else {
         evaluatedValue = eval(parseFloat(expression)); // Evaluate the input as a mathematical expression
      }
      return evaluatedValue;
   };

   const commitChanges = async () => {
      let sanitizedValue = tempValue;
      if (
         props.typeId === arrow.Type.Int ||
         props.typeId === arrow.Type.Float
      ) {
         try {
            // Validate expression using a regex
            const evaluatedValue = await sanitizeMathExpression(tempValue);
            // Ensure the result is numeric
            if (!isNaN(evaluatedValue)) {
               sanitizedValue =
                  props.typeId === arrow.Type.Int
                     ? Math.trunc(evaluatedValue) // Truncate for integers
                     : evaluatedValue; // Use as-is for floats
            } else {
               sanitizedValue = null; // Handle invalid expressions
            }
         } catch (error) {
            console.error(`Error evaluating expression: ${tempValue}`, error);
            sanitizedValue = null; // Handle errors in eval gracefully
         }
      }
      // Only run the code when the values differ, or for Int when they are equal
      if (
         sanitizedValue !== props.content ||
         props.typeId === arrow.Type.Int ||
         props.typeId === arrow.Type.Float
      ) {
         try {
            await props.handleEditCell(
               props.rows,
               props.columns,
               props.rowIndex,
               props.columnIndex,
               sanitizedValue, // Pass the sanitized value
            );
         } catch (error) {
            console.error("Error committing changes:", error);
         }
      }
   };

   const handleDatetimeChange = (e) => {
      // Handle the value and propagate it back to the parent
      const newDatetime = e.target.value; // New datetime in "YYYY-MM-DDTHH:mm" format
      const timestamp = new Date(newDatetime).toISOString(); // Convert back to ISO timestamp
      props.handleEditCell(
         props.rows,
         props.columns,
         props.rowIndex,
         props.columnIndex,
         timestamp,
      );
   };

   switch (props.typeId) {
      case arrow.Type.Utf8:
         return (
            <>
               <input
                  type={"text"}
                  value={tempValue}
                  style={{
                     border: "none",
                     padding: "10px",
                     fontSize: "16px",
                     width: "100%",
                     boxSizing: "border-box", // Makes sure padding doesn’t affect the width
                     outline: "none", // Removes the blue outline when input is focused
                     cursor: "text",
                     background: "transparent",
                     height: "auto",
                     minHeight: "50px",
                     display: "block",
                  }}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown} // Trigger handleEditCell when Enter is pressed
               />
            </>
         );
      case arrow.Type.Float:
         return (
            <>
               <input
                  type={"text"}
                  value={tempValue}
                  style={{
                     border: "none",
                     padding: "10px",
                     fontSize: "16px",
                     width: "100%",
                     boxSizing: "border-box", // Makes sure padding doesn’t affect the width
                     outline: "none", // Removes the blue outline when input is focused
                     cursor: "text",
                     background: "transparent",
                     height: "auto",
                     minHeight: "50px",
                     display: "block",
                  }}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown} // Trigger handleEditCell when Enter is pressed
               />
            </>
         );
      case arrow.Type.Int:
         return (
            <>
               <input
                  type={"text"}
                  value={tempValue}
                  style={{
                     border: "none",
                     padding: "10px",
                     fontSize: "16px",
                     width: "100%",
                     boxSizing: "border-box", // Makes sure padding doesn’t affect the width
                     outline: "none", // Removes the blue outline when input is focused
                     cursor: "text",
                     background: "transparent",
                     height: "auto",
                     minHeight: "50px",
                     display: "block",
                  }}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown} // Trigger handleEditCell when Enter is pressed
               />
            </>
         );
      case arrow.Type.Timestamp: {
         // Convert content (timestamp) to the required "datetime-local" format
         const formattedDate = convertToDatetimeLocal(content);
         return (
            <>
               <input
                  type={"datetime-local"}
                  value={formattedDate}
                  style={{
                     border: "none",
                     padding: "10px",
                     fontSize: "16px",
                     width: "100%",
                     boxSizing: "border-box", // Makes sure padding doesn’t affect the width
                     outline: "none", // Removes the blue outline when input is focused
                     cursor: "text",
                     background: "transparent",
                     height: "auto",
                     minHeight: "50px",
                     display: "block",
                  }}
                  onChange={handleDatetimeChange}
               />
            </>
         );
      }
      default:
         return <div>Error no input for that type</div>;
   }
});

EditableTableCell.displayName = "EditableTableCell";

export default EditableTableCell;
